import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Building2, Trash2, AlertTriangle, X, Save, ExternalLink } from 'lucide-react';
import { api } from '../../api/client';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';

type Tenant = {
  id: string;
  name: string;
  taxId: string;
  contactEmail: string | null;
  suspended: boolean;
  createdAt: string;
  locationCount: number;
  userCount: number;
  salesCount: number;
};

export function TenantsPage() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Tenant | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await api.get<Tenant[]>('/admin/tenants');
      setTenants(rows);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await api.del(`/admin/tenants/${deleteTarget.id}`);
    refresh();
  };

  return (
    <div>
      <header style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>Tenanti (klijenti)</h1>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
            Klijenti platforme - svaki ima svoje lokacije, operatere, račune
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => setCreating(true)} style={btnPrimary}>
          <Plus size={14} /> Novi tenant
        </button>
      </header>

      {error && (
        <div style={errorBox}>
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      <div style={{ background: 'var(--color-bg-surface)', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--color-border)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--color-bg)' }}>
                <th style={th}>Naziv</th>
                <th style={th}>PIB</th>
                <th style={{ ...th, textAlign: 'right' }}>Lokacije</th>
                <th style={{ ...th, textAlign: 'right' }}>Korisnici</th>
                <th style={{ ...th, textAlign: 'right' }}>Računi</th>
                <th style={{ ...th, width: 100, textAlign: 'right' }}>Akcije</th>
              </tr>
            </thead>
            <tbody>
              {!loading && tenants.length === 0 && (
                <tr><td colSpan={6} style={{ ...td, textAlign: 'center', padding: 40, color: 'var(--color-text-muted)' }}>
                  <Building2 size={32} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
                  <div>Nema tenant-a</div>
                </td></tr>
              )}
              {tenants.map((t) => (
                <tr key={t.id} onClick={() => navigate(`/tenants/${t.id}`)} style={{ cursor: 'pointer' }}>
                  <td style={{ ...td, color: 'var(--color-text-strong)', fontWeight: 600 }}>
                    {t.name}
                    {t.suspended && <span style={{ marginLeft: 8, fontSize: 9, padding: '1px 6px', borderRadius: 8, background: 'rgba(239,68,68,0.2)', color: '#fca5a5', fontWeight: 700 }}>SUSP</span>}
                  </td>
                  <td style={{ ...td, fontVariantNumeric: 'tabular-nums', color: 'var(--color-text-muted)' }}>{t.taxId || '-'}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--color-text-muted)' }}>{t.locationCount}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--color-text-muted)' }}>{t.userCount}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--color-text-muted)' }}>{t.salesCount}</td>
                  <td style={{ ...td, textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => navigate(`/tenants/${t.id}`)} style={iconBtn} title="Otvori"><ExternalLink size={13} /></button>
                    <button onClick={() => setDeleteTarget(t)} style={{ ...iconBtn, color: '#fca5a5' }} title="Obriši"><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {creating && (
        <TenantCreateDialog
          onClose={() => setCreating(false)}
          onSaved={(newTenant) => { setCreating(false); refresh(); navigate(`/tenants/${newTenant.id}`); }}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Obriši tenant"
          message={`Da li ste sigurni da želite trajno da obrišete tenant "${deleteTarget.name}"? Sve njegove lokacije, korisnici i podaci će biti uklonjeni. Ova akcija se ne može opozvati.`}
          confirmLabel="Obriši tenant"
          variant="danger"
          onConfirm={confirmDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

function TenantCreateDialog({ onClose, onSaved }: { onClose: () => void; onSaved: (t: { id: string }) => void }) {
  useEscapeToClose(onClose);
  const [name, setName] = useState('');
  const [taxId, setTaxId] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [jbkjs, setJbkjs] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('Srbija');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  // Klijent user
  const [username, setUsername] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  // Trial: probni paket kase, samo L-PFR (ADR-060) ili bez probe
  const [trial, setTrial] = useState<'pos' | 'lpfr' | 'none'>('pos');
  const [lpfrDevices, setLpfrDevices] = useState(1);
  // Referral
  const [referralCode, setReferralCode] = useState('');
  const [referralLookup, setReferralLookup] = useState<{ referrerName: string } | null>(null);
  const [referralError, setReferralError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Naziv je obavezan'); return; }
    setSaving(true);
    setError(null);
    try {
      const body: any = {
        name: name.trim(),
        taxId: taxId.trim(),
        registrationNumber: registrationNumber.trim() || undefined,
        jbkjs: jbkjs.trim() || undefined,
        address: address.trim() || undefined,
        city: city.trim() || undefined,
        country: country.trim() || 'Srbija',
        contactEmail: contactEmail || undefined,
        contactPhone: contactPhone || undefined,
        activateTrial: trial === 'pos',
        referralCode: referralCode.trim() || undefined,
      };
      if (username && password && fullName) {
        body.user = { username: username.trim(), email: userEmail || undefined, fullName: fullName.trim(), password };
      }
      const result = await api.post<{ tenant: { id: string } }>('/admin/tenants', body);
      if (trial === 'lpfr') {
        try {
          await api.post(`/admin/tenants/${result.tenant.id}/licenses`, {
            moduleCode: 'LPFR',
            seatsAllowed: lpfrDevices,
            validUntil: new Date(Date.now() + LPFR_TRIAL_DAYS * 86_400_000).toISOString(),
            isTrial: true,
          });
        } catch (e: any) {
          // Tenant je već napravljen - ne blokiraj; licenca se dodaje ručno u tabu Licence.
          alert(`Tenant je napravljen, ali probna L-PFR licenca nije: ${e?.data?.message || e?.message || 'greška'}. Dodaj je u tabu Licence.`);
        }
      }
      onSaved(result.tenant);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>
        <header style={dialogHeader}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--color-text-strong)' }}>Novi tenant (klijent)</h3>
          <button onClick={onClose} style={closeBtn}><X size={16} /></button>
        </header>
        <form onSubmit={handleSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14, maxHeight: '70vh', overflow: 'auto' }}>
          <Section title="Klijent (firma)">
            <Field label="Naziv klijenta *"><input type="text" value={name} onChange={(e) => setName(e.target.value)} autoFocus required style={input} /></Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
              <Field label="PIB"><input type="text" value={taxId} onChange={(e) => setTaxId(e.target.value)} placeholder="123456789" style={input} /></Field>
              <Field label="Matični broj (MB)"><input type="text" value={registrationNumber} onChange={(e) => setRegistrationNumber(e.target.value)} placeholder="12345678" style={input} /></Field>
              <Field label="JBKJS"><input type="text" value={jbkjs} onChange={(e) => setJbkjs(e.target.value)} placeholder="za budz. korisnike" style={input} /></Field>
            </div>
            <Field label="Adresa"><input type="text" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Bulevar Kralja Aleksandra 73" style={input} /></Field>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
              <Field label="Grad"><input type="text" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Beograd" style={input} /></Field>
              <Field label="Zemlja"><input type="text" value={country} onChange={(e) => setCountry(e.target.value)} style={input} /></Field>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Field label="Kontakt telefon"><input type="text" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} style={input} /></Field>
              <Field label="Kontakt email"><input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} style={input} /></Field>
            </div>
          </Section>

          <Section title="POS klijent korisnik (opciono)">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Field label="Username"><input type="text" value={username} onChange={(e) => setUsername(e.target.value)} style={input} /></Field>
              <Field label="Lozinka"><input type="text" value={password} onChange={(e) => setPassword(e.target.value)} minLength={6} style={input} /></Field>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Field label="Puno ime"><input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} style={input} /></Field>
              <Field label="Email (login)"><input type="email" value={userEmail} onChange={(e) => setUserEmail(e.target.value)} style={input} /></Field>
            </div>
          </Section>

          <Section title="Trial">
            {([
              ['pos', 'Kasa (TRIAL) - 30-dnevni trial paket (POS_BASIC + MAGACIN + IZVEŠTAJI)'],
              ['lpfr', `Samo L-PFR - proba ${LPFR_TRIAL_DAYS} dana, licenca po uređaju`],
              ['none', 'Bez probe'],
            ] as const).map(([value, label]) => (
              <label key={value} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--color-text)' }}>
                <input type="radio" name="trial" value={value} checked={trial === value} onChange={() => setTrial(value)} />
                {label}
              </label>
            ))}
            {trial === 'lpfr' && (
              <div style={{ maxWidth: 180 }}>
                <Field label="Broj uređaja">
                  <input type="number" min={1} max={100} value={lpfrDevices} onChange={(e) => setLpfrDevices(Math.max(1, Number(e.target.value) || 1))} style={input} />
                </Field>
              </div>
            )}
          </Section>

          <Section title="Referal kod (opciono)">
            <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 8 }}>
              Ako je ovaj klijent došao preko referala, unesi kod. Nagrada se pali kad plati PRVI račun. Klijent može i sam da upiše kod kasnije, u „Popusti i nagrade → Referal program“ - ali samo dok ne plati prvi račun.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                value={referralCode}
                onChange={(e) => { setReferralCode(e.target.value.toUpperCase()); setReferralLookup(null); setReferralError(null); }}
                placeholder="MIL24-A7F2"
                style={{ ...input, fontFamily: 'Menlo, Consolas, monospace', letterSpacing: 1 }}
              />
              <button
                type="button"
                disabled={!referralCode.trim()}
                onClick={async () => {
                  setReferralError(null);
                  setReferralLookup(null);
                  try {
                    const res = await api.get<{ referrerName?: string; notFound?: boolean }>(`/admin/referral/resolve/${encodeURIComponent(referralCode.trim())}`);
                    if (res.notFound || !res.referrerName) setReferralError('Kod ne postoji');
                    else setReferralLookup({ referrerName: res.referrerName });
                  } catch (e: any) {
                    setReferralError(e?.data?.message || 'Greška pri provera koda');
                  }
                }}
                style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                Proveri
              </button>
            </div>
            {referralLookup && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#86efac' }}>
                ✓ Referrer: {referralLookup.referrerName}
              </div>
            )}
            {referralError && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#fca5a5' }}>
                {referralError}
              </div>
            )}
          </Section>

          {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
            <button type="button" onClick={onClose} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>Otkaži</button>
            <button type="submit" disabled={saving || !name.trim()} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: saving ? 0.5 : 1 }}>
              <Save size={14} /> {saving ? 'Čuvanje...' : 'Sačuvaj'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 14, background: 'var(--color-bg)', borderRadius: 8, border: '1px solid var(--color-border)' }}>
      <h4 style={{ margin: 0, fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{title}</h4>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500 }}>{label}</label>
      {children}
    </div>
  );
}

/** Isto kao `LPFR_TRIAL_DAYS` na API-ju (proba pri samostalnoj registraciji). */
const LPFR_TRIAL_DAYS = 14;

const th: React.CSSProperties = { padding: '12px 14px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '12px 14px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border)' };
const btnPrimary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { padding: '9px 16px', borderRadius: 6, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', padding: 6, marginLeft: 4, borderRadius: 4, cursor: 'pointer' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', padding: 24 };
const dialog: React.CSSProperties = { width: '100%', maxWidth: 720, background: 'var(--color-bg-surface)', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: '1px solid var(--color-border)' };
const dialogHeader: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--color-border)' };
const closeBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', padding: 4 };
const input: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--color-bg)', color: 'var(--color-text-strong)', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, outline: 'none' };
